"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildMonthlyReport = buildMonthlyReport;
/**
 * The doctor report.
 *
 * The single most important output. Denise's promise was that a chart like this makes
 * a drug reaction possible to isolate — "every Dr who saw this found it extremely
 * helpful" — and this is the artefact that has to deliver on it.
 *
 * Three things a clinician needs that the paper chart could not do:
 *   - dose times and symptom times on ONE timeline, so a correlation is visible rather
 *     than reconstructed
 *   - extra doses and impairment-amplifying substances called out, not buried
 *   - provenance: which times were observed in the moment, and which were recalled
 *     later or reported by a caregiver
 */
const domain_js_1 = require("./domain.js");
const schedule_js_1 = require("./schedule.js");
const time_js_1 = require("./time.js");
function buildMonthlyReport(input) {
    const { patient, medications, from, to, now } = input;
    const events = input.events.filter((e) => e.voidedAt == null);
    const scheduled = medications.filter((m) => !(0, schedule_js_1.isAsNeeded)(m.scheduleType));
    const dates = (0, time_js_1.logicalDateRange)(from, to);
    const byDate = new Map();
    for (const event of events) {
        const list = byDate.get(event.logicalDate) ?? [];
        list.push(event);
        byDate.set(event.logicalDate, list);
    }
    const days = dates.map((logicalDate) => {
        const dayEvents = (byDate.get(logicalDate) ?? []).sort((a, b) => a.takenAtUtc.localeCompare(b.takenAtUtc));
        const cells = [];
        for (const med of scheduled) {
            const occupied = new Set(med.slots.map((s) => (0, schedule_js_1.dayPartFor)(s.localTime)));
            for (const dayPart of schedule_js_1.DAY_PARTS) {
                if (!occupied.has(dayPart)) {
                    cells.push({ medId: med.medId, dayPart, notScheduled: true });
                    continue;
                }
                const slotIds = med.slots
                    .filter((s) => (0, schedule_js_1.dayPartFor)(s.localTime) === dayPart)
                    .map((s) => s.slotId);
                const logged = dayEvents.find((e) => e.eventType === 'DOSE_TAKEN' &&
                    e.medId === med.medId &&
                    e.slotId != null &&
                    slotIds.includes(e.slotId));
                const cell = { medId: med.medId, dayPart, notScheduled: false };
                if (logged) {
                    cell.takenAtLocal = logged.localTime ?? (0, time_js_1.formatInZone)(logged.takenAtUtc, patient.tz, 'HH:mm');
                    if (logged.extraDose === true)
                        cell.extraDose = true;
                }
                cells.push(cell);
            }
        }
        return {
            logicalDate,
            weekdayLabel: weekdayLabelFor(logicalDate),
            cells,
            otherText: dayEvents
                .filter((e) => e.eventType === 'EXTRA_SUBSTANCE' || isExtraDoseEvent(e))
                .map((e) => describeOther(e, patient.tz))
                .join('; '),
            symptomText: dayEvents
                .filter((e) => e.eventType === 'SYMPTOM')
                .map((e) => describeSymptom(e, patient.tz))
                .join('; '),
        };
    });
    const timeline = events
        .map((e) => toTimelineEntry(e, patient.tz))
        .sort((a, b) => a.takenAtUtc.localeCompare(b.takenAtUtc));
    /*
     * Adherence counts only days that have actually happened.
     *
     * Running a report mid-month otherwise divides by the whole month and reports
     * something like "4%" — which reads as catastrophic non-compliance when the real
     * answer is "it is the 7th". A number a clinician might act on must not be an
     * artefact of the calendar.
     */
    const today = (0, time_js_1.resolveLogicalDate)(now, patient.tz, patient.dayStartHour);
    const elapsed = dates.filter((d) => d <= today);
    const scheduledDoses = elapsed.reduce((total, date) => total +
        scheduled.reduce((perMed, med) => perMed + (appliesOn(med, date) ? med.slots.length : 0), 0), 0);
    const loggedDoses = events.filter((e) => e.eventType === 'DOSE_TAKEN' && e.slotId != null && e.extraDose !== true).length;
    return {
        patientName: patient.displayName,
        isTest: patient.isTest ?? false,
        tz: patient.tz,
        from,
        to,
        generatedAt: now.toISOString(),
        medications: medications.map(toMedicationLine),
        dayParts: schedule_js_1.DAY_PARTS.map((key) => ({ key, label: schedule_js_1.DAY_PART_LABELS[key] })),
        days,
        timeline,
        extraDoses: timeline.filter((t) => t.extraDose === true),
        amplifiers: timeline.filter((t) => t.amplifier === true),
        redFlags: timeline.filter((t) => t.redFlag === true),
        adherence: {
            scheduledDoses,
            loggedDoses,
            percent: scheduledDoses === 0 ? 0 : Math.round((loggedDoses / scheduledDoses) * 100),
            countedThrough: elapsed[elapsed.length - 1] ?? from,
        },
        footnotes: buildFootnotes(medications, timeline),
    };
}
function appliesOn(med, logicalDate) {
    if (!med.daysOfWeek)
        return true;
    const [y, m, d] = logicalDate.split('-').map(Number);
    if (y === undefined || m === undefined || d === undefined)
        return true;
    const names = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
    return med.daysOfWeek.includes(names[new Date(Date.UTC(y, m - 1, d, 12)).getUTCDay()] ?? '');
}
function toMedicationLine(med) {
    const line = {
        medId: med.medId,
        label: (0, schedule_js_1.medLabelFor)(med),
        purpose: med.purpose ?? '',
        scheduleSummary: (0, schedule_js_1.isAsNeeded)(med.scheduleType)
            ? 'As needed'
            : med.slots.map((s) => `${s.label} ${s.localTime}`).join(', '),
        confirmationStatus: med.confirmationStatus,
    };
    if (med.confirmationNote)
        line.provenanceNote = med.confirmationNote;
    return line;
}
/**
 * Footnotes a clinician needs in order to weigh what they are reading.
 *
 * A schedule inferred from a photograph, or a time recorded from a device whose clock
 * was wrong, should not look identical to one that is certain.
 */
function buildFootnotes(medications, timeline) {
    const notes = [];
    for (const med of medications) {
        if (med.confirmationStatus === 'UNCONFIRMED') {
            notes.push(`${med.name}: the prescribed frequency is NOT established. The label reads ` +
                `"${med.labelText ?? 'SEE DIRECTIONS'}". Doses are recorded as taken; no schedule is assumed.`);
        }
        else if (med.confirmationStatus === 'PROBABLE') {
            notes.push(`${med.name}: this schedule was read from a photograph of the label and has not ` +
                `been confirmed with the pharmacy.`);
        }
    }
    if (timeline.some((t) => t.actorRole === 'CAREGIVER')) {
        notes.push('Entries marked "caregiver" were reported by a family member rather than recorded ' +
            'by the patient at the time, and are therefore recall rather than observation.');
    }
    const skewed = timeline.filter((t) => t.timeQuality !== 'GOOD');
    if (skewed.length > 0) {
        notes.push(`${String(skewed.length)} entr${skewed.length === 1 ? 'y was' : 'ies were'} recorded on a ` +
            `device whose clock differed from the server; those times may be approximate.`);
    }
    return notes;
}
function isExtraDoseEvent(event) {
    return event.eventType === 'DOSE_TAKEN' && event.extraDose === true;
}
function toTimelineEntry(event, tz) {
    const localTime = event.localTime ?? (0, time_js_1.formatInZone)(event.takenAtUtc, tz, 'HH:mm');
    const base = {
        logicalDate: event.logicalDate,
        localTime,
        takenAtUtc: event.takenAtUtc,
        actorRole: event.actorRole ?? 'SELF',
        timeQuality: event.timeQuality ?? 'GOOD',
    };
    switch (event.eventType) {
        case 'DOSE_TAKEN':
            return {
                ...base,
                kind: 'DOSE',
                label: event.medId ?? 'dose',
                ...(event.extraDose === true ? { extraDose: true } : {}),
            };
        case 'SYMPTOM':
            return {
                ...base,
                kind: 'SYMPTOM',
                label: (event.symptoms ?? []).map(humanise).join(', '),
                ...(event.redFlag === true ? { redFlag: true } : {}),
                ...(event.severity ? { severity: event.severity } : {}),
            };
        case 'EXTRA_SUBSTANCE':
            return {
                ...base,
                kind: 'EXTRA',
                label: event.substanceName ?? humanise(event.substanceKey ?? 'something'),
                ...(event.substanceKey && (0, domain_js_1.isAmplifier)(event.substanceKey) ? { amplifier: true } : {}),
            };
        default:
            return { ...base, kind: 'VITALS', label: describeVitals(event) };
    }
}
function describeVitals(event) {
    const parts = [];
    if (event.pulseBpm !== undefined)
        parts.push(`pulse ${String(event.pulseBpm)}`);
    if (event.systolic !== undefined && event.diastolic !== undefined) {
        parts.push(`BP ${String(event.systolic)}/${String(event.diastolic)}`);
    }
    return parts.join(', ') || 'reading';
}
function describeOther(event, tz) {
    const time = event.localTime ?? (0, time_js_1.formatInZone)(event.takenAtUtc, tz, 'HH:mm');
    if (isExtraDoseEvent(event))
        return `${time} EXTRA ${event.medId ?? 'dose'}`;
    const amount = event.quantity !== undefined ? ` x${String(event.quantity)}` : '';
    return `${time} ${event.substanceName ?? humanise(event.substanceKey ?? '')}${amount}`;
}
function describeSymptom(event, tz) {
    const time = event.localTime ?? (0, time_js_1.formatInZone)(event.takenAtUtc, tz, 'HH:mm');
    const flag = event.redFlag === true ? ' [911 ADVISED]' : '';
    return `${time} ${(event.symptoms ?? []).map(humanise).join(', ')}${flag}`;
}
function humanise(value) {
    return value.toLowerCase().replace(/_/g, ' ');
}
function weekdayLabelFor(logicalDate) {
    const [y, m, d] = logicalDate.split('-').map(Number);
    if (y === undefined || m === undefined || d === undefined)
        return '';
    const names = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return `${names[new Date(Date.UTC(y, m - 1, d, 12)).getUTCDay()] ?? ''} ${String(d)}`;
}
//# sourceMappingURL=report.js.map